import { type QueryKey } from '@tanstack/react-query';
import { memo, type ReactElement, useMemo } from 'react';
import { useAuth } from '@/auth/useAuth';
import { featuresGridConfig } from '@/features/features/grid/gridConfig';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { type GridFilterModel, type GridSortModel } from '@/ui/primitives';
import { buildFeaturesColumns } from './featuresColumns';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { FeatureDto, QueryResult } from '@flowqa/shared';

type FeaturesTableProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<FeatureDto>>;
  queryKey?: QueryKey;
  onRowsChange?: (rows: FeatureDto[]) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
};

const FeaturesTableComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize = 5,
  disablePageSizeSelect,
  noRowsText = 'No features found',
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  onRowsChange,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
}: FeaturesTableProps): ReactElement => {
  const { session } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const defaultColumns = useMemo(
    () => buildFeaturesColumns({ onProjectSelect: setSelectedProjectId }),
    [setSelectedProjectId],
  );

  const gridConfig = featuresGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey ?? 'features-table',
    fixedFilters,
    initialSortModel,
    initialFilterModel,
  });

  return (
    <ServerDataTable<FeatureDto>
      queryPath="features/query"
      {...gridConfig}
      queryFn={queryFn ?? gridConfig.queryFn}
      queryKey={queryKey ?? gridConfig.queryKey}
      columns={defaultColumns}
      getRowId={(row) => row.id}
      token={session?.tokens.accessToken ?? null}
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      pageSizeOptions={pageSizeOptions}
      initialPageSize={initialPageSize}
      disablePageSizeSelect={disablePageSizeSelect}
      noRowsText={noRowsText}
      disableCellFocus
      skeletonGetRowHeight={null}
      showLastUpdated={showLastUpdated}
      autoHeight
      onRowsChange={onRowsChange}
      onQueryInput={onQueryInput}
      onDataUpdatedAt={onDataUpdatedAt}
    />
  );
};

export const FeaturesTable = memo(FeaturesTableComponent);
